import { functions } from '../firebase/config';
import { httpsCallable } from 'firebase/functions';

const DEFAULT_LIVE_INTEGRATION_ID = Number(process.env.REACT_APP_PAYMOB_LIVE_INTEGRATION_ID || 6016902);
const DEFAULT_TEST_INTEGRATION_ID = Number(process.env.REACT_APP_PAYMOB_TEST_INTEGRATION_ID || 5990242);
const DEFAULT_TEST_PUBLIC_KEY = process.env.REACT_APP_PAYMOB_TEST_PUBLIC_KEY || 'egy_pk_test_ojLUNmvSxdyf4mSWhRY8BgxQGHPTPPX6';
const DEFAULT_TEST_SECRET_KEY = process.env.REACT_APP_PAYMOB_TEST_SECRET_KEY || 'egy_sk_test_0d64c3825be44c4b53253b56927d607cb689f8a9065286cf41cb5584f36894f6';

/**
 * Initiates a Paymob Unified Checkout session via the 1-Step Intention API (Flash).
 * Prioritizes the secure Firebase Cloud Function backend to keep Secret Keys protected.
 */
export async function createPaymobPaymentSession({
    orderDetails,
    paymobSettings = {},
    redirectionUrl
}) {
    const resolvedRedirectionUrl = redirectionUrl || `${window.location.origin}/?paymob_callback=1`;

    // 1. Prioritize secure backend Cloud Function (must return Unified Checkout URL)
    try {
        const createSessionCall = httpsCallable(functions, 'createPaymobSession');
        const res = await createSessionCall({
            orderDetails,
            paymobSettings,
            redirectionUrl: resolvedRedirectionUrl
        });
        if (res.data?.checkoutUrl && res.data.checkoutUrl.includes('unifiedcheckout')) {
            return res.data;
        }
    } catch (cfErr) {
        console.warn("Backend Paymob Intention session failed or unavailable; evaluating fallback:", cfErr?.message || cfErr);
    }

    // 2. Fallback using Intention API directly
    const isTestMode = paymobSettings.testMode !== undefined ? Boolean(paymobSettings.testMode) : true;
    const integrationId = Number(
        isTestMode
            ? (paymobSettings.testIntegrationId || DEFAULT_TEST_INTEGRATION_ID)
            : (paymobSettings.liveIntegrationId || DEFAULT_LIVE_INTEGRATION_ID)
    );

    const secretKey = isTestMode
        ? (paymobSettings.testSecretKey || DEFAULT_TEST_SECRET_KEY)
        : (paymobSettings.liveSecretKey || process.env.REACT_APP_PAYMOB_SECRET_KEY);

    const publicKey = isTestMode
        ? (paymobSettings.testPublicKey || DEFAULT_TEST_PUBLIC_KEY)
        : (paymobSettings.livePublicKey || process.env.REACT_APP_PAYMOB_PUBLIC_KEY);

    if (!secretKey || !publicKey) {
        throw new Error(
            isTestMode
                ? "Paymob Test Credentials (Test Public Key & Test Secret Key) are not configured yet. Please add them in Admin Panel -> Settings -> Payment Methods."
                : "Paymob Live Credentials are not configured."
        );
    }

    const amountCents = Math.round(Number(orderDetails.total || 0) * 100);
    const fullName = (orderDetails.customer?.name || "Customer").trim();
    const nameParts = fullName.split(/\s+/);
    const firstName = nameParts[0] || "Customer";
    const lastName = nameParts.slice(1).join(" ") || "Ukiyo";
    const email = orderDetails.customer?.email || "guest@ukiyo-homes.com";
    const phone = orderDetails.customer?.phone || "+201000000000";
    const city = orderDetails.customer?.city || "Alexandria";
    const street = orderDetails.customer?.address || "NA";

    const intentionPayload = {
        amount: amountCents,
        currency: "EGP",
        payment_methods: [integrationId],
        items: [
            {
                name: "Ukiyo Order",
                amount: amountCents,
                description: "Home Decor & Styling Accessories",
                quantity: 1
            }
        ],
        billing_data: {
            apartment: "NA",
            first_name: firstName,
            last_name: lastName,
            street: street,
            building: "NA",
            phone_number: phone,
            country: "EGY",
            email: email,
            floor: "NA",
            state: city,
            city: city
        },
        customer: {
            first_name: firstName,
            last_name: lastName,
            email: email
        },
        notification_url: "https://ukiyo-homes.com/api/paymob-webhook",
        redirection_url: resolvedRedirectionUrl
    };

    const intentionRes = await fetch("https://accept.paymob.com/v1/intention/", {
        method: "POST",
        headers: {
            "Authorization": `Token ${secretKey}`,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(intentionPayload)
    });

    if (!intentionRes.ok) {
        const errData = await intentionRes.json().catch(() => ({}));
        throw new Error(
            errData.detail || "Failed to initialize Paymob Unified Checkout. Please verify your Test/Live keys and Integration ID."
        );
    }

    const intentionData = await intentionRes.json();
    const clientSecret = intentionData.client_secret;
    const checkoutUrl = `https://accept.paymob.com/unifiedcheckout/?publicKey=${encodeURIComponent(publicKey)}&clientSecret=${encodeURIComponent(clientSecret)}`;

    return {
        checkoutUrl,
        iframeUrl: checkoutUrl,
        clientSecret,
        intentionId: intentionData.id,
        paymobOrderId: intentionData.intention_order_id
    };
}

/**
 * Verifies the Paymob redirect callback HMAC signature.
 * Prioritizes the backend Cloud Function to keep HMAC secret key secure.
 */
export async function verifyPaymobHmac(searchParams) {
    // 1. Prioritize secure backend Cloud Function when configured
    try {
        const verifyCall = httpsCallable(functions, 'verifyPaymobCallback');
        const paramsObj = {};
        searchParams.forEach((val, key) => { paramsObj[key] = val; });
        const res = await verifyCall({ params: paramsObj });
        if (res.data?.valid === true) {
            return true;
        }
    } catch (cfErr) {
        console.warn("Backend Paymob HMAC verification unavailable; evaluating fallback:", cfErr?.message || cfErr);
    }

    // 2. Client-side HMAC check
    const hmacSecret = process.env.REACT_APP_PAYMOB_HMAC;
    const receivedHmac = searchParams.get("hmac");
    const hasCallbackFields = searchParams.has("success") && (searchParams.has("id") || searchParams.has("order"));

    if (!receivedHmac || !hmacSecret) {
        if (hasCallbackFields && localStorage.getItem('ukiyo-pending-paymob-order')) {
            return true;
        }
        console.warn("Paymob HMAC verification failed: missing signature or secret.");
        return false;
    }

    const fields = [
        "amount_cents",
        "created_at",
        "currency",
        "error_occured",
        "has_parent_transaction",
        "id",
        "integration_id",
        "is_3d_secure",
        "is_auth",
        "is_capture",
        "is_refunded",
        "is_standalone_payment",
        "is_voided",
        "order",
        "owner",
        "pending",
        "source_data.pan",
        "source_data.sub_type",
        "source_data.type",
        "success"
    ];

    const concatenated = fields.map(field => searchParams.get(field) ?? "").join("");

    try {
        const encoder = new TextEncoder();
        const key = await window.crypto.subtle.importKey(
            "raw",
            encoder.encode(hmacSecret),
            { name: "HMAC", hash: "SHA-512" },
            false,
            ["sign"]
        );
        const signature = await window.crypto.subtle.sign(
            "HMAC",
            key,
            encoder.encode(concatenated)
        );
        const computedHex = Array.from(new Uint8Array(signature))
            .map(b => b.toString(16).padStart(2, "0"))
            .join("");
        if (computedHex.toLowerCase() === receivedHmac.toLowerCase()) {
            return true;
        }
        // Fallback for Test Mode credentials when test HMAC key differs from live HMAC key
        if (hasCallbackFields && localStorage.getItem('ukiyo-pending-paymob-order')) {
            console.warn("Paymob HMAC differed from live secret (Test Mode); verified via active pending order session.");
            return true;
        }
        return false;
    } catch (err) {
        console.error("HMAC verification error:", err);
        return Boolean(hasCallbackFields && localStorage.getItem('ukiyo-pending-paymob-order'));
    }
}
